import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { Dynamic, type DynamicAction } from '@shared/registry/dynamic';

import { type ProcessStatusCardConfig } from './process-status-card.types';

@Component({
  selector: 'app-process-status-card',
  templateUrl: './process-status-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessStatusCard {
  readonly config = input.required<ProcessStatusCardConfig>();

  readonly action = output<DynamicAction>();

  protected readonly variant = computed(() => this.config().variant ?? 'default');
  protected readonly hasBorder = computed(() => this.config().hasBorder ?? false);
  protected readonly isHighlighted = computed(() => this.variant() === 'highlighted');

  protected readonly chipConfig = computed(() => {
    const chip = this.config().chip;
    if (!chip) return null;
    return {
      ...chip,
      variant: this.isHighlighted() ? ('subtle' as const) : chip.variant,
    };
  });

  protected readonly statusMessageConfig = computed(() => ({
    text: this.config().statusMessage,
    size: 'xl' as const,
    fontWeight: 'semibold' as const,
    color: 'var(--processstatuscard-status-message-color)',
    fontFamily: 'var(--processstatuscard-font-family)',
  }));

  protected readonly bodyMessageConfig = computed(() => {
    const body = this.config().bodyMessage;
    if (!body) return null;
    return {
      text: body,
      size: 'sm' as const,
      fontWeight: 'bold' as const,
      color: 'var(--processstatuscard-body-message-color)',
      fontFamily: 'var(--processstatuscard-font-family)',
    };
  });

  protected readonly progressConfig = computed(() => this.config().progress);

  protected onButtonClick(event: DynamicAction): void {
    this.action.emit(event);
  }
}
